import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from './auth.service';
import { PlatformService } from './platform.service';
import { IDLE_TIMEOUT_MS, IDLE_WARNING_MS, RETURN_URL_PARAM } from '../constants/session-lifecycle.config';
import { GENERIC_SESSION_EXPIRED_MESSAGE } from '../utils/session-expiry-message';

export interface IdleWarningState {
  /** True while the "Still there?" warning modal should be shown. */
  visible: boolean;
  /** Seconds remaining before the forced sign-out fires. Counts down for the
   *  modal's aria-live region and visible countdown text. */
  secondsLeft: number;
}

/**
 * Idle timeout, app-wide (admin + public/applicant areas — no idSite
 * conditionals; every tenant gets the same 4h idle window per
 * session-lifecycle.config.ts).
 *
 * Activity (pointerdown/keydown/scroll/visibilitychange) resets a 4h timer.
 * At the 4h mark a "Still there?" warning is shown with a 60s countdown
 * BEFORE signing out, so a user who is still at the keyboard (just not
 * actively clicking/typing — reading a long document, filling a large form
 * slowly) gets a chance to say so. If they don't respond, or the countdown
 * completes, this triggers a clean sign-out.
 *
 * Dead-session incident guard (2026-07-17, see VersionWatchdogService /
 * auth.interceptor.ts doc comments): a forced sign-out must NEVER discard an
 * in-progress upload or unsaved form edit, and must NEVER hard-reload the
 * page. This service delegates the actual sign-out to AuthService.logout(),
 * which does NOT reload the page — it clears state and re-initializes the
 * site in place — and fires AuthService.emitSessionExpired() first (the SAME
 * signal project-registration's helpers/auth.helpers.ts already listens for
 * on a network-triggered dead session) so any component protecting unsaved
 * state (its own dirty-form guard, its own interruptedUploads queue) gets
 * the identical chance to save/queue before the sign-out proceeds.
 * IdleService does not and must not know about any specific component's
 * upload/dirty state — it only emits the same signal those components
 * already listen to.
 *
 * This is a genuine forced sign-out + redirect to /login (unlike
 * HeartbeatService's auth-failure path, which reuses the in-place recovery
 * modal instead — see that service's doc comment for why the two cases
 * warrant different endings): idle timeout means the user really did walk
 * away, confirmed by a full warning + unanswered countdown, so routing them
 * to a fresh sign-in (with returnUrl preserved) is the right outcome rather
 * than leaving a stale page waiting indefinitely for a modal no one will see.
 */
@Injectable({ providedIn: 'root' })
export class IdleService {
  private lastActivity = Date.now();
  private idleCheckTimer: ReturnType<typeof setInterval> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private started = false;
  private readonly activityEvents = ['pointerdown', 'keydown', 'scroll'] as const;
  private boundOnActivity = () => this.recordActivity();
  private boundOnVisibility = () => {
    if (document.visibilityState === 'visible') this.recordActivity();
  };

  private warningSubject = new BehaviorSubject<IdleWarningState>({ visible: false, secondsLeft: 0 });
  /** Subscribed by IdleWarningModalComponent (hosted once, at app-root). */
  warning$ = this.warningSubject.asObservable();

  constructor(
    private zone: NgZone,
    private authService: AuthService,
    private router: Router,
    private platformService: PlatformService,
  ) {}

  /** Called once from app-root's ngOnInit — mirrors VersionWatchdogService.start(). */
  start(): void {
    // No tab, no activity events, no document to watch on the server — a
    // fresh SSR render per request has nothing that could go "idle".
    if (!this.platformService.isBrowser) return;
    if (this.started) return;
    this.started = true;
    this.recordActivity();

    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach((ev) =>
        window.addEventListener(ev, this.boundOnActivity, { passive: true }));
      document.addEventListener('visibilitychange', this.boundOnVisibility);
      // Poll every second rather than a single long setTimeout — a single
      // setTimeout for 4h is subject to background-tab throttling/clamping
      // in the same way the old token-health single-fire setTimeout was
      // (see AuthService.startTokenHealthMonitor's comment) and would drift.
      this.idleCheckTimer = setInterval(() => this.checkIdle(), 1000);
    });
  }

  /** Stops all timers/listeners — used by specs and available for teardown. */
  stop(): void {
    this.started = false;
    this.activityEvents.forEach((ev) => window.removeEventListener(ev, this.boundOnActivity));
    document.removeEventListener('visibilitychange', this.boundOnVisibility);
    if (this.idleCheckTimer) { clearInterval(this.idleCheckTimer); this.idleCheckTimer = null; }
    this.clearCountdown();
  }

  private recordActivity(): void {
    this.lastActivity = Date.now();
    // Activity while the warning is showing counts as "still there" — same
    // effect as clicking Stay Signed In, without requiring the click.
    if (this.warningSubject.getValue().visible) {
      this.dismissWarning();
    }
  }

  private checkIdle(): void {
    if (this.warningSubject.getValue().visible) return; // countdown owns the clock now
    const idleFor = Date.now() - this.lastActivity;
    if (idleFor >= IDLE_TIMEOUT_MS) {
      this.beginWarning();
    }
  }

  private beginWarning(): void {
    let secondsLeft = Math.ceil(IDLE_WARNING_MS / 1000);
    this.zone.run(() => this.warningSubject.next({ visible: true, secondsLeft }));
    this.countdownTimer = setInterval(() => {
      secondsLeft -= 1;
      if (secondsLeft <= 0) {
        this.clearCountdown();
        this.zone.run(() => this.forceSignOut());
        return;
      }
      this.zone.run(() => this.warningSubject.next({ visible: true, secondsLeft }));
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) { clearInterval(this.countdownTimer); this.countdownTimer = null; }
  }

  /** "Stay signed in" — dismisses the warning and resets the idle clock.
   *  Called from the modal's button and from Escape/keyboard activation. */
  stayActive(): void {
    this.dismissWarning();
  }

  private dismissWarning(): void {
    this.clearCountdown();
    this.lastActivity = Date.now();
    this.warningSubject.next({ visible: false, secondsLeft: 0 });
  }

  /** Countdown reached zero with no response — force a clean sign-out. */
  private forceSignOut(): void {
    this.warningSubject.next({ visible: false, secondsLeft: 0 });
    this.signOutForIdleTimeout();
  }

  /**
   * Idle-timeout sign-out: reuses AuthService.logout() — no parallel logout
   * implementation — with redirect suppressed so this method can do its own
   * returnUrl-preserving navigation to /login instead of AuthService's
   * default /locations redirect (which would lose the "come back here after
   * signing in" destination and doesn't apply to admin routes anyway).
   *
   * Fires emitSessionExpired() BEFORE clearing auth, same ordering as
   * auth.interceptor.ts's handleSessionExpired — any component with an
   * active dirty-form/upload guard gets the chance to persist state via the
   * exact same signal path the 2026-07-17 fix already wired up.
   */
  signOutForIdleTimeout(): void {
    // Already on the sign-in page: there is nothing to sign out of and nowhere
    // to come back to. Capturing router.url here is what produced
    // /login?returnUrl=/login?returnUrl=/login... — each idle tick nested the
    // previous URL inside the next, the address grew without bound, and the
    // router's pushState calls were hammered until the browser refused them
    // ("The operation is insecure", 605 occurrences in 6h across FilmLEX and
    // Las Cruces, 2026-07-29). Users sat in a looping login page.
    if (this.isOnLoginRoute()) return;

    this.authService.emitSessionExpired();
    const returnUrl = this.router.url;
    this.authService.logout(false).finally(() => {
      this.router.navigate(['/login'], {
        // Never carry a login URL as the destination — a returnUrl pointing at
        // /login is the loop in one field.
        queryParams: {
          ...(this.isLoginUrl(returnUrl) ? {} : { [RETURN_URL_PARAM]: returnUrl }),
          reason: GENERIC_SESSION_EXPIRED_MESSAGE,
        },
      });
    });
  }

  /** True when the given URL is the sign-in route (with or without params). */
  private isLoginUrl(url: string): boolean {
    const path = String(url || '').split('?')[0].split('#')[0];
    return path === '/login' || path.endsWith('/login');
  }

  /** True when the app is currently sitting on the sign-in route. */
  private isOnLoginRoute(): boolean {
    return this.isLoginUrl(this.router.url);
  }
}
